<template>
  <v-app :theme="isDark ? 'dark' : 'light'">
    <!-- Barra superior -->
    <v-app-bar app :color="isDark ? '#0e0e10' : '#1f2937'" dark flat elevate-on-scroll height="64" class="px-6">
      <!-- Logo -->
      <router-link to="/inicio">
        <v-img alt="Logo Empresa" class="shrink" contain :src="isDark ? darkLogo : lightLogo" width="140"
          style="cursor: pointer" />
      </router-link>

      <v-spacer />

      <!-- Toggle Auto-rotación (SOLO en PowerBI view) -->
      <div v-if="mostrarAutoRotateToggle" class="autorotate mr-3">
        <v-icon small class="mr-2">mdi-file-document-outline</v-icon>

        <v-switch v-model="autoRotateEnabled" inset dense hide-details class="autorotate-switch"
          label="Pasar Páginas" />
      </div>

      <!-- Botón pantalla completa (SOLO en PowerBI view) -->
      <v-btn
        v-if="mostrarAutoRotateToggle"
        icon
        class="mr-2"
        color="white"
        @click="toggleFullscreenFromApp"
        :title="isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'"
      >
        <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </v-btn>

      <!-- Usuario logueado -->
      <div v-if="usuario" class="d-flex align-center">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn text v-bind="attrs" v-on="on" class="pa-0">
              <v-icon left small>mdi-account</v-icon>
              <span class="mr-2">{{ usuario.name }} {{ usuario.surname }}</span>
              <v-icon small>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <v-list dense style="min-width: 160px">
            <v-list-item v-if="puedeVerUsuarios" @click="$router.push('/usuarios')">
              <v-list-item-icon>
                <v-icon small>mdi-account-multiple</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Usuarios</v-list-item-title>
            </v-list-item>
            <v-list-item @click="cerrarSesion">
              <v-list-item-icon>
                <v-icon small color="red">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="red--text">Cerrar sesión</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Modo oscuro (NO en dashboards) -->
      <v-btn v-if="!enVistaDashboards" icon @click="toggleTheme">
        <v-icon>{{ isDark ? "mdi-weather-sunny" : "mdi-weather-night" }}</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <!-- Snackbar global (notificaciones) -->
    <v-snackbar v-model="notify.show" :color="notify.color" timeout="3500" top right>
      <v-icon left v-if="notify.icon">{{ notify.icon }}</v-icon>
      {{ notify.text }}
      <v-btn text @click="notify.show = false">Cerrar</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { eventBus } from "@/helpers/eventBus";

export default {
  name: "App",
  data() {
    return {
      isDark: localStorage.getItem("theme") === "dark",
      lightLogo: require("@/assets/Logo Experticia Blanco Celeste.png"),
      darkLogo: require("@/assets/Logo Experticia Blanco Celeste.png"),
      usuario: JSON.parse(sessionStorage.getItem("usuario")),

      notify: {
        show: false,
        text: "",
        color: "info",
        icon: "",
      },

      autoRotateEnabled: JSON.parse(localStorage.getItem("autoRotateEnabled") ?? "true"),
      isFullscreen: false,
    };
  },

  created() {
    // Sigue funcionando para notificaciones internas por eventBus (si las usas en otras partes)
    eventBus.$on("notify", ({ text, color, icon }) => {
      this.notify.text = text || "";
      this.notify.color = color || "info";
      this.notify.icon = icon || "";
      this.notify.show = true;
    });

    // Escuchar cambios de fullscreen desde DashboardsPowerBIView
    eventBus.$on("fullscreen-changed", (isFullscreen) => {
      this.isFullscreen = isFullscreen;
    });
  },

  computed: {
    esAdminTI() {
      return (this.usuario?.rol || "").trim().toLowerCase() === "adminti";
    },
    esAdmin() {
      return (this.usuario?.rol || "").trim().toLowerCase() === "admin";
    },
    puedeVerUsuarios() {
      return this.esAdminTI || this.esAdmin;
    },
    mostrarAutoRotateToggle() {
      return this.$route && this.$route.path.startsWith("/dashboards/");
    },
    enVistaDashboards() {
      return this.$route && this.$route.path.startsWith("/dashboards/");
    },
  },

  watch: {
    // cada vez que cambia la ruta
    $route() {
      this.mostrarPendingNotify();

      // 🔒 Forzar modo claro SOLO en /dashboards/*
      if (this.enVistaDashboards) {
        this.$vuetify.theme.dark = false;
      } else {
        this.$vuetify.theme.dark = this.isDark;
      }
    },

    autoRotateEnabled(val) {
      localStorage.setItem("autoRotateEnabled", JSON.stringify(val));
      eventBus.$emit("auto-rotate-toggle", val);
      window.dispatchEvent(new CustomEvent("auto-rotate-toggle", { detail: val }));
    },
  },

  methods: {
    // ✅ CLAVE: muestra snackbar aunque haya redirección (router guard)
    mostrarPendingNotify() {
      const raw = sessionStorage.getItem("pendingNotify");
      if (!raw) return;

      try {
        const n = JSON.parse(raw);
        this.notify.text = n.text || "";
        this.notify.color = n.color || "info";
        this.notify.icon = n.icon || "";
        this.notify.show = true;
      } catch (e) {
        // ignore
      } finally {
        sessionStorage.removeItem("pendingNotify");
      }
    },

    toggleTheme() {
      if (this.enVistaDashboards) return; // 🔒 no permitir toggle en dashboards

      this.isDark = !this.isDark;
      this.$vuetify.theme.dark = this.isDark;
      localStorage.setItem("theme", this.isDark ? "dark" : "light");
    },

    cerrarSesion() {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("usuario");
      this.usuario = null;
      this.$router.push("/");
    },

    toggleFullscreenFromApp() {
      eventBus.$emit("toggle-fullscreen");
    },
  },

  mounted() {
    // ✅ Si parto en /dashboards/*, forzar claro
    this.$vuetify.theme.dark = this.enVistaDashboards ? false : this.isDark;

    this.mostrarPendingNotify();

    eventBus.$on("usuario-logeado", (usuario) => {
      this.usuario = usuario;
    });

    eventBus.$emit("auto-rotate-toggle", this.autoRotateEnabled);
    window.dispatchEvent(new CustomEvent("auto-rotate-toggle", { detail: this.autoRotateEnabled }));
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap");

body {
  font-family: "Poppins", sans-serif;
  margin: 0;
}

.v-main {
  background: linear-gradient(135deg, #7484ab, #080a0e);
  min-height: 100vh;
  color: #ffffff;
}

.v-btn {
  transition: all 0.3s ease;
  font-weight: 500;
}

.v-btn:hover {
  transform: scale(1.04);
  opacity: 0.95;
}

.theme--dark.v-application {
  background-color: transparent !important;
  color: #e0e0e0 !important;
}

.theme--dark .v-card,
.theme--dark .v-container,
.theme--dark .v-list,
.theme--dark .v-tabs {
  color: #ffffff !important;
}

.v-list {
  font-size: 0.85rem;
}

.v-list-item {
  min-height: 32px !important;
}

.v-list-item-title {
  font-size: 0.85rem;
}

/* Estilos del toggle en el topbar */
.autorotate {
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
}

.autorotate-switch {
  margin: 0;
  padding: 0;
}
</style>